import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { BUTTON_SIZES, GhostButton, SecondaryButton } from 'ui/button';
import { TableToggle } from 'ui/indexPages/tables/tableToggle';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';

import { TEMPLATE_INDEX_FILTERS, MESSAGE_MEDIUM_FILTERS } from '../constants';
import { TEMPLATE_INDEX_VIEWS } from '../hooks/useViewControl';
import { TemplateIndexFilter } from './TemplateIndexFilter';
import { WideInputText } from './TemplateIndexPage.styled';
import { displayContent } from './content';
import { IndexTabsList, IndexTabTrigger, TabsGroup } from 'ui/tabsGroup';
import { usePaginationContext } from 'utils/contexts/pagination';
import { FolderClosed } from 'ui/icons/action/FolderClosed';

export const StyledMeta = styled.div`
  align-items: stretch;
  display: flex;
  margin-bottom: ${spacing.double};
  justify-content: space-between;

  @media all and (max-width: 1124px) {
    /* grid-template-columns: 1fr;
    row-gap: ${spacing.base}; */
  }
`;

export const MetaSection = styled.div`
  flex: 0 0 auto;
  > * + * {
    margin-left: ${spacing.half};
  }
`;

export const LeftMeta = MetaSection;

export const RightMeta = styled(MetaSection)`
  align-items: center;
  display: flex;
  justify-content: flex-end;

  > * {
    flex: 0 0 auto;
    margin-left: ${spacing.half};
  }

  [data-test='input-wrapper'] {
    height: ${pxToRem(40)};
  }
`;

type ControlsProps = {
  activeView: string;
  clearSearch?: () => void;
  setActiveView: (view: TEMPLATE_INDEX_VIEWS) => void;
  canClearFilters: boolean;
  flatSavedFilters: any[];
  handleClearFilters: (e: React.MouseEvent) => void;
  handleFilterChange: (filters: TEMPLATE_INDEX_FILTERS) => void;
  handleNewFolder: (e: React.MouseEvent) => void;
  onTabChange: (tabId: any) => void;
  selectedTab: string;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
};

export const Controls = ({
  activeView,
  clearSearch,
  setActiveView,
  canClearFilters,
  flatSavedFilters,
  handleClearFilters,
  handleFilterChange,
  handleNewFolder,
  onTabChange,
  selectedTab,
  searchInputValue,
  setSearchInputValue,
}: ControlsProps) => {
  const isEmbeddedMessagingCoreEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const messageMediumFilters = MESSAGE_MEDIUM_FILTERS.filter((filter) =>
    !isEmbeddedMessagingCoreEnabled ? filter.id !== 'Embedded' : filter,
  ).filter((filter) => (!isWhatsAppFFOn ? filter.id !== 'WhatsApp' : filter));

  const { setCurrentPage } = usePaginationContext();

  return (
    <StyledMeta>
      <LeftMeta>
        <TabsGroup data-test="templates-tabs-filter" value={selectedTab}>
          <IndexTabsList>
            {messageMediumFilters.map(({ id: fid, label }) => (
              <IndexTabTrigger
                key={fid}
                value={fid}
                onClick={() => {
                  setCurrentPage(1);
                  onTabChange(fid);
                }}
              >
                {label}
              </IndexTabTrigger>
            ))}
          </IndexTabsList>
        </TabsGroup>
      </LeftMeta>
      <RightMeta>
        {handleNewFolder && (
          <SecondaryButton size={BUTTON_SIZES.LARGE} onClick={handleNewFolder}>
            <FolderClosed />
            <span>New folder</span>
          </SecondaryButton>
        )}
        {canClearFilters && (
          <GhostButton
            data-test="clear-template-filters-button"
            id="clear-template-filters-button"
            onClick={handleClearFilters}
            tabIndex={0}
            size={BUTTON_SIZES.LARGE}
          >
            {displayContent.clear}
          </GhostButton>
        )}
        <TemplateIndexFilter savedSelections={flatSavedFilters} onSelect={handleFilterChange} />
        <WideInputText
          data-test="template-index-search"
          id="template-search-input"
          handleClear={clearSearch}
          onChange={setSearchInputValue}
          placeholder={displayContent.searchPlaceholder}
          value={searchInputValue}
        />
        <TableToggle
          data-test="template-index-view-toggle"
          activeView={activeView}
          setActiveView={(view) => setActiveView(view as TEMPLATE_INDEX_VIEWS)}
          views={TEMPLATE_INDEX_VIEWS}
        />
      </RightMeta>
    </StyledMeta>
  );
};
